import { useState, useEffect, useCallback } from 'react'
import { State } from '../hooks/gameReducer'
import { sendStateToAPI } from './api-v2'

// Throttle interval: 10 seconds
const THROTTLE_INTERVAL = 10 * 1000

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error'

interface SyncState {
  status: SyncStatus
  lastSyncedAt: Date | null
  nextSyncAt: Date | null
  error: string | null
}

// Global state for sync
let pendingState: State | null = null
let lastSyncedStateStr: string | null = null
let lastSyncTime: number = 0
let throttleTimeout: ReturnType<typeof setTimeout> | null = null
let listeners: Set<(state: SyncState) => void> = new Set()
let pendingOnSuccess: ((data: any) => void) | undefined = undefined
let skipBeforeUnloadWarning = false // Flag to skip beforeunload warning

let currentSyncState: SyncState = {
  status: 'idle',
  lastSyncedAt: null,
  nextSyncAt: null,
  error: null,
}

const notifyListeners = () => {
  listeners.forEach((listener) => listener({ ...currentSyncState }))
}

const updateSyncState = (updates: Partial<SyncState>) => {
  currentSyncState = { ...currentSyncState, ...updates }
  notifyListeners()
}

/**
 * Check if there are unsaved changes
 */
export const hasPendingChanges = (): boolean => {
  if (!pendingState) return false
  const currentStateStr = JSON.stringify(pendingState)
  return currentStateStr !== lastSyncedStateStr
}

/**
 * Throttled state sync - syncs immediately if 10+ seconds since last sync,
 * otherwise schedules sync for when throttle period ends
 */
export const queueStateSync = (
  state: State,
  onSuccess?: (data: any) => void
): void => {
  pendingState = state
  pendingOnSuccess = onSuccess

  // Check if there are actual changes
  const currentStateStr = JSON.stringify(state)
  if (currentStateStr === lastSyncedStateStr) {
    return // No changes, don't queue
  }

  const now = Date.now()
  const timeSinceLastSync = now - lastSyncTime

  if (timeSinceLastSync >= THROTTLE_INTERVAL) {
    // Throttle period passed, sync immediately
    syncStateNow(onSuccess)
  } else {
    // Schedule sync for when throttle period ends
    if (!throttleTimeout) {
      const timeUntilNextSync = THROTTLE_INTERVAL - timeSinceLastSync
      const nextSyncTime = new Date(now + timeUntilNextSync)

      updateSyncState({
        status: 'pending',
        nextSyncAt: nextSyncTime,
        error: null,
      })

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null
        if (hasPendingChanges()) {
          syncStateNow(pendingOnSuccess)
        }
      }, timeUntilNextSync)
    } else {
      // Already scheduled, just update pending state
      updateSyncState({ status: 'pending' })
    }
  }
}

/**
 * Immediately sync state to Firebase (for game end or page unload)
 */
export const syncStateNow = async (
  onSuccess?: (data: any) => void,
  explicitState?: State
): Promise<boolean> => {
  // Use explicit state if provided, otherwise use pendingState
  const stateToSync = explicitState || pendingState

  if (!stateToSync) return false

  const currentStateStr = JSON.stringify(stateToSync)

  // Skip if nothing changed since last sync
  if (currentStateStr === lastSyncedStateStr) {
    updateSyncState({ status: 'synced' })
    return true
  }

  // Skip if already syncing
  if (currentSyncState.status === 'syncing') {
    return false
  }

  updateSyncState({ status: 'syncing', error: null })

  try {
    const data = await sendStateToAPI(stateToSync)
    lastSyncedStateStr = currentStateStr
    lastSyncTime = Date.now()

    // Clear pending timeout since we just synced
    if (throttleTimeout) {
      clearTimeout(throttleTimeout)
      throttleTimeout = null
    }

    updateSyncState({
      status: 'synced',
      lastSyncedAt: new Date(),
      nextSyncAt: null,
    })

    if (onSuccess) {
      onSuccess(data)
    }

    return true
  } catch (error) {
    console.error('Error syncing state:', error)
    updateSyncState({
      status: 'error',
      error: error instanceof Error ? error.message : 'Sync failed',
    })
    return false
  }
}

/**
 * Force sync and return a promise (for beforeunload)
 */
export const forceSyncBeforeUnload = (): boolean => {
  if (!hasPendingChanges() || !pendingState) {
    return true
  }

  // Use sendBeacon for reliable unload sync
  try {
    const API_URL = process.env.REACT_APP_API_V2_URL || '/api-v2.php'
    const url = API_URL.startsWith('http')
      ? API_URL
      : `${window.location.protocol}//${window.location.host}${API_URL}`

    // sendBeacon is more reliable for unload events
    if (navigator.sendBeacon) {
      const blob = new Blob(
        [
          JSON.stringify({
            id: pendingState.id,
            state: JSON.stringify(pendingState),
          }),
        ],
        { type: 'application/json' }
      )
      navigator.sendBeacon(url, blob)
      return true
    }

    // Fallback to sync XHR (blocking)
    const data = new FormData()
    data.append('id', pendingState.id)
    data.append('state', JSON.stringify(pendingState))
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, false) // false = synchronous
    xhr.send(data)
    return true
  } catch (error) {
    console.error('Error in forceSyncBeforeUnload:', error)
    return false
  }
}

/**
 * Initialize beforeunload handler
 */
export const initBeforeUnloadHandler = (): (() => void) => {
  const handler = (event: BeforeUnloadEvent) => {
    // Skip warning if flag is set (e.g., during profile switch)
    if (skipBeforeUnloadWarning) {
      return
    }

    if (hasPendingChanges()) {
      // Try to sync
      forceSyncBeforeUnload()

      // Show browser warning
      event.preventDefault()
      event.returnValue =
        'Mentetlen változtatások vannak. Biztosan elhagyod az oldalt?'
      return event.returnValue
    }
  }

  window.addEventListener('beforeunload', handler)

  return () => {
    window.removeEventListener('beforeunload', handler)
  }
}

/**
 * Set flag to skip beforeunload warning (e.g., during profile switch)
 */
export const skipNextBeforeUnloadWarning = () => {
  skipBeforeUnloadWarning = true
}

/**
 * Clear sync timeout (for cleanup)
 */
export const clearSyncTimeout = (): void => {
  if (throttleTimeout) {
    clearTimeout(throttleTimeout)
    throttleTimeout = null
  }
}

/**
 * React hook to subscribe to sync state changes
 */
export const useSyncStatus = () => {
  const [syncState, setSyncState] = useState<SyncState>(currentSyncState)

  useEffect(() => {
    // Add listener
    listeners.add(setSyncState)

    // Set initial state
    setSyncState({ ...currentSyncState })

    // Cleanup
    return () => {
      listeners.delete(setSyncState)
    }
  }, [])

  const syncNow = useCallback(async () => {
    return syncStateNow()
  }, [])

  return {
    ...syncState,
    syncNow,
    hasPendingChanges: hasPendingChanges(),
  }
}
